import { useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Modal, Spin } from 'antd';
import { FormProvider, useForm } from 'react-hook-form';

import { useUpdateAdvantageMutation } from '@/api/endpoints/mainPages';
import { DataSection, InputField, SelectField } from '@/shared/components';
import { useApiRequest } from '@/shared/hooks';
import { EMainPageAdvantageIcons } from '@/shared/types/mainPage';

import { ADVANTAGE_VALIDATION_SCHEMA } from '../../consts';
import type { IAdvantageItemData, IFormData } from '../../types';
import { IconSelectLabel } from '../IconSelectLabel/IconSelectLabel';

import styles from './AdvantageModals.module.scss';

interface IProp {
  pageId: number;
  isModalOpen: boolean;
  handleCancel: VoidFunction;
  handleClearOperateItem: VoidFunction;
  itemToOperate: IAdvantageItemData;
}

export const EditAdvantageModal = ({ pageId, isModalOpen, handleCancel, itemToOperate }: IProp) => {
  const [updateAdvantage, { isLoading: isAdvantageUpdating }] = useUpdateAdvantageMutation();

  const sendApiRequest = useApiRequest();

  const methods = useForm<IFormData>({
    defaultValues: {
      title: itemToOperate ? itemToOperate.title : '',
      description: itemToOperate ? itemToOperate.description : '',
      icon: itemToOperate ? itemToOperate.icon : undefined,
    },
    resolver: yupResolver(ADVANTAGE_VALIDATION_SCHEMA),
  });

  const { handleSubmit } = methods;

  const handleUpdateAdvantage = handleSubmit(({ title, description, icon }) => {
    if (icon) {
      sendApiRequest({
        successMessage: 'Преимущество успешно изменено',
        apiRequestCallback: () =>
          updateAdvantage({
            mainPageId: pageId,
            advantageId: itemToOperate.id,
            data: {
              title,
              description,
              icon,
            },
          }),
        successCallback: handleCancel,
      });
    }
  });

  const badgeIconsList = useMemo(
    () =>
      Object.values(EMainPageAdvantageIcons).map((iconType) => ({
        value: iconType,
        label: <IconSelectLabel iconType={iconType} />,
      })),
    [],
  );

  return (
    <Modal
      width={600}
      open={isModalOpen}
      onCancel={handleCancel}
      footer={null}
      centered
    >
      <Spin spinning={isAdvantageUpdating}>
        <div className={styles.wrapper}>
          <FormProvider {...methods}>
            <p className={styles.title}>Изменение преимущества школы</p>
            <div className={styles['content-wrapper']}>
              <DataSection
                titleText="Заголовок преимущества"
                isTitleRequired
              >
                <InputField
                  fieldName="title"
                  placeholder="Заголовок"
                  shouldEnableLengthCounter
                  maxLengthCount={200}
                />
              </DataSection>
              <DataSection
                titleText="Описание преимущества"
                isTitleRequired
              >
                <InputField
                  fieldName="description"
                  placeholder="Описание"
                  shouldEnableLengthCounter
                  maxLengthCount={500}
                />
              </DataSection>
              <DataSection
                titleText="Иконка преимущества"
                isTitleRequired
              >
                <SelectField
                  fieldName="icon"
                  size="large"
                  virtual={false}
                  showSearch={false}
                  placeholder="Выбрать иконку"
                  options={badgeIconsList}
                />
              </DataSection>
            </div>
            <div className={styles['buttons-wrapper']}>
              <Button
                size="large"
                onClick={handleCancel}
                className={styles.button}
                danger
              >
                Отмена
              </Button>
              <Button
                size="large"
                onClick={handleUpdateAdvantage}
                type="primary"
                className={styles.button}
                disabled={isAdvantageUpdating}
              >
                Сохранить
              </Button>
            </div>
          </FormProvider>
        </div>
      </Spin>
    </Modal>
  );
};
